import Yup from '@yup';
import { Dayjs } from 'dayjs';
import { Leg } from '@api/Trinium/Endpoints/Leg';
import { LegType } from '@api/Trinium/types';
import { MutatableLegRaw } from '@api/Trinium/Endpoints/Leg/types';
import { apiIsoStringFormat, parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { getObjectChanges, removeUndefinedProperties } from '@/Infrastructure/Utilities/object.type';
import { getDateFieldValue } from '@/ConsignmentDetails/types';

export type ContainerFormik = Record<string, string | number | boolean | LegType | Dayjs | null | undefined> & {
	fullAvailableDate: Dayjs | null;
	emptyAvailableDate: Dayjs | null;
	chassisAvailableDate: Dayjs | null;
	lastFreeDate: Dayjs | null;
	cutoffDate?: Dayjs | null;

	legType?: keyof typeof LegType;
	containerNumber?: string;
	size?: string;
	type?: string;
	streetTurn: Dayjs | null;
	pieces: number | null;
	weightInPounds: number | null;
	weightInKg: number | null;
	sealNo: string;

	chassisNumber?: string | null;
	isOwned: boolean;
	catTransType?: string;

	vessel?: string;
	voyage?: string;
	firstReceiveDate?: Dayjs | null;
};

export const emptyContainerForm: ContainerFormik = {
	fullAvailableDate: null,
	emptyAvailableDate: null,
	chassisAvailableDate: null,
	lastFreeDate: null,
	streetTurn: null,
	pieces: null,
	weightInPounds: null,
	weightInKg: null,
	sealNo: '',
	isOwned: false
};

export const containerFormValidationSchema = Yup.object<ContainerFormik>().shape({
	emptyAvailableDate: Yup.date().nullable().typeError('Invalid Date Time').containerMaxRange(),

	fullAvailableDate: Yup.date().nullable().typeError('Invalid Date Time').containerMaxRange(),

	chassisAvailableDate: Yup.date().nullable().typeError('Invalid Date Time').containerMaxRange(),

	sealNo: Yup.string().nullable().typeError('Invalid String Format').max(55, 'Seal # exceeds max length (55)'),

	containerNumber: Yup.string().nullable().max(13, 'Container # exceeds max length (13)')
});

export const containerApiFieldMapping: Record<keyof ContainerFormik, string> = {
	chassisNumber: 'chassisId',
	emptyAvailableDate: 'emptyAvailDate',
	chassisAvailableDate: 'chassisAvailDate',
	containerNumber: 'cntrId',
	fullAvailableDate: 'fullAvailDate',
	isOwned: 'isChassisOwned',
	streetTurn: 'streetTurnDate',
	legType: 'type',
	pieces: 'pcs',
	sealNo: 'puSealNo',
	dehireId: 'dehireId',
	size: 'cntrSize',
	type: 'type',
	weightInKg: 'wgtKg',
	weightInPounds: 'wgtLb',
	catTransType: 'catTransType',
	lastFreeDate: 'lastFreeDate',
	cutoffDate: 'cutoffDate'
};

export function mapLegToContainerForm(leg?: Leg): ContainerFormik {
	return {
		legType: leg?.type,
		containerNumber: leg?.cntrId ?? '',
		size: leg?.cntrSize,
		type: leg?.cntrType,
		streetTurn: parseDayJs(leg?.streetTurnDate?.unix),
		chassisNumber: leg?.chassisId ?? null,
		isOwned: leg?.isChassisOwned ?? false,
		fullAvailableDate: parseDayJs(leg?.fullAvailableDate?.unix),
		emptyAvailableDate: parseDayJs(leg?.emptyAvailableDate?.unix),
		chassisAvailableDate: parseDayJs(leg?.chassisAvailableDate?.unix),
		pieces: leg?.pcs ?? null,
		weightInPounds: leg?.wgtLb ?? null,
		weightInKg: leg?.wgtLb ? convertPoundsToKilograms(leg.wgtLb) : null,
		sealNo: leg?.puSealNo ?? '',
		dehireId: leg?.dehireId ?? '',
		lastFreeDate: parseDayJs(leg?.lastFreeDate?.unix),
		cutoffDate: parseDayJs(leg?.cutoffDate?.unix),
		catTransType: leg?.job?.catTransType,
		vessel: leg?.job?.vslName,
		voyage: leg?.job?.voyage,
		firstReceiveDate: parseDayJs(leg?.firstReceiveDate?.unix)
	};
}

export function mapContainerToMutableLeg(formValues: ContainerFormik, initialFormValues?: ContainerFormik): MutatableLegRaw {
	/**
	 * Determine what changes have been made to the form
	 */
	const valuesToPost = getObjectChanges(formValues, initialFormValues);
	const legType = getEnumValueByKey(LegType, formValues.legType);

	/**
	 * Map form changes to a MutableLegRaw for API consumption
	 */
	const updates: MutatableLegRaw = {
		cntrId: valuesToPost.containerNumber,
		chassisId: valuesToPost.chassisNumber,
		pcs: valuesToPost.pieces,
		wgtLb: valuesToPost.weightInPounds,
		puSealNo: valuesToPost.sealNo,
		streetTurnDate: valuesToPost.streetTurn !== undefined ? (valuesToPost.streetTurn?.format(apiIsoStringFormat) ?? null) : undefined,
		/** If full available is included in the changes, set it. Or if last free day changed on an FCL, set it. */
		fullAvailDate: getDateFieldValue(
			valuesToPost.fullAvailableDate,
			initialFormValues?.fullAvailableDate,
			!!valuesToPost.lastFreeDate && legType === LegType.FCL
		),
		/** If empty available is included in the changes, set it. Or if last free day changed on an ECL, set it. */
		emptyAvailDate: getDateFieldValue(
			valuesToPost.emptyAvailableDate,
			initialFormValues?.emptyAvailableDate,
			!!valuesToPost.lastFreeDate && legType === LegType.ECL
		),
		/** If last free day is included in the changes set it. Or if either availability changed, set it. */
		lastFreeDate: getDateFieldValue(
			valuesToPost.lastFreeDate,
			initialFormValues?.lastFreeDate,
			!!(valuesToPost.fullAvailableDate || valuesToPost.emptyAvailableDate)
		),
		cutoffDate:
			valuesToPost.cutoffDate !== undefined // cutoff is set through lastFreeDate for Export
				? (valuesToPost.cutoffDate?.format(apiIsoStringFormat) ?? null)
				: undefined,
		chassisAvailDate:
			valuesToPost.chassisAvailableDate !== undefined ? (valuesToPost.chassisAvailableDate?.format(apiIsoStringFormat) ?? null) : undefined
	};

	return removeUndefinedProperties(updates);
}

export function convertPoundsToKilograms(weightInPounds: number | null) {
	if (!weightInPounds) return 0;
	const convertedValue = Math.round(weightInPounds * 0.45359237);
	return convertedValue < 1 ? 1 : convertedValue;
}

export function convertKilogramsToPounds(weightInKg: number | null) {
	if (!weightInKg) return 0;
	const convertedValue = Math.round(weightInKg * 2.20462262);
	return convertedValue < 1 ? 1 : convertedValue;
}

export function getWeightDisplay(weight: number | null | undefined, isMetric: boolean) {
	if (!weight) return '';
	return `${weight.toLocaleString()} ${isMetric ? 'kg' : 'lb'}`;
}
